import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { getLeadNoContact } from "../Admin/Utilities/api";
import { normalizeDate } from "../../utilities";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState } from "../../components/UI/States";

const WithoutContact = () => {
  const [leads, setLeads] = useState([]);
  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "customerName",
      },
      {
        Header: "Email",
        accessor: "customerEmail",
      },
      {
        Header: "Phone",
        accessor: "customerPhone",
      },
      {
        Header: "Address",
        accessor: "customerAddress",
      },
      // Add "Date Added" column
      {
        Header: "Date Added",
        accessor: "entryDate",
        Cell: ({ value }) => normalizeDate(value),
      },
      // Add "Assigned To" column
      {
        Header: "Assigned To",
        accessor: "assignedSalesRep.Name",
        Cell: ({ cell: { value }, row: { original } }) => {
          if (value) {
            return value;
          } else if (
            original.assignedManager &&
            original.assignedManager.Name
          ) {
            return original.assignedManager.Name;
          } else {
            return "Not Assigned";
          }
        },
      },
    ],
    []
  );

  useEffect(() => {
    getLeadNoContact(setLeads); // Call getLeadNoContact with setLeads
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns,
      data: leads,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="2xl">
        <PageHeader
          icon={PhoneDisabledIcon}
          tone="emerald"
          eyebrow="Sales & Leads"
          title="Webleads Without Contact"
          description="Leads with no email, phone, or voicemail contact logged."
        />

        {leads.length === 0 ? (
          <div className="app-card rounded-2xl border">
            <EmptyState
              icon={InboxOutlinedIcon}
              title="No leads to show"
              description="Leads without any logged contact appear here."
            />
          </div>
        ) : (
          <div className="app-card overflow-hidden rounded-2xl border">
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="app-table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="cursor-pointer select-none hover:text-[color:var(--app-text)]"
                        >
                          {column.render("Header")}
                          <span aria-hidden="true" className="ml-1 inline-block w-3">
                            {column.isSorted
                              ? column.isSortedDesc
                                ? "▼"
                                : "▲"
                              : ""}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="app-divider flex items-center justify-between gap-3 border-t px-4 py-2.5">
              <span className="app-muted text-xs font-medium tabular-nums">
                Page{" "}
                <strong className="text-[color:var(--app-text)]">
                  {pageIndex + 1} of {pageOptions.length}
                </strong>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  aria-label="Previous page"
                  className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  aria-label="Next page"
                  className="app-icon-btn disabled:pointer-events-none disabled:opacity-40"
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        )}
      </PageShell>
    </div>
  );
};

export default WithoutContact;
